import type {EstablishedAhpSession, EstablishedAhpSessionViewSnapshot} from '@github-ui/ahp-session-client/contracts'
import {useSyncExternalStore, type ReactNode} from 'react'
import {
  CopilotResultPresentationContext,
  useCopilotResultPresentationSelection,
  useCopilotResultPresentationView,
} from '@github-ui/copilot-browser-latency/use-copilot-result-presentation'
import type {CopilotResultPresentationSelection} from '@github-ui/copilot-browser-latency/copilot-result-presentation'
import {
  CopilotClientRecoveryContext,
  useCopilotClientRecovery,
} from '@github-ui/copilot-browser-latency/use-copilot-client-recovery'
import {useCopilotUiReadiness} from '@github-ui/copilot-browser-latency/copilot-ui-readiness-context'

import {
  EstablishedAhpSessionContext,
  EstablishedAhpSessionSnapshotContext,
} from '../contexts/EstablishedAhpSessionContext'

const EMPTY_ESTABLISHED_AHP_SNAPSHOT: EstablishedAhpSessionViewSnapshot = {
  state: undefined,
  status: 'idle',
  reachable: false,
  error: undefined,
  modelFailures: undefined,
  provisioning: undefined,
  sessionState: undefined,
  passive: false,
  passiveKnown: false,
  steeringMessages: [],
}

export interface EstablishedAhpSessionProviderProps {
  readonly session?: EstablishedAhpSession
  readonly resultPresentation?: CopilotResultPresentationSelection
  /** Registers recovery ownership when descendants report composer controls. */
  readonly recoveryControls?: boolean
  readonly children: ReactNode
}

const subscribeToEmptySnapshot = () => () => undefined
const getEmptySnapshot = () => EMPTY_ESTABLISHED_AHP_SNAPSHOT

/**
 * Makes an optional host-owned established AHP session available to shared surfaces.
 * Acquisition, recovery, and disposal remain outside this provider.
 */
export function EstablishedAhpSessionProvider({
  session,
  resultPresentation,
  recoveryControls = false,
  children,
}: EstablishedAhpSessionProviderProps) {
  const surface = useCopilotUiReadiness()?.open.surface ?? 'unknown'
  const recovery = useCopilotClientRecovery(recoveryControls ? session?.identity : undefined, surface)
  const localPresentation = useCopilotResultPresentationSelection(
    resultPresentation ? undefined : session?.identity,
    surface,
  )
  const presentation = useCopilotResultPresentationView(resultPresentation ?? localPresentation)
  const snapshot = useSyncExternalStore(
    session?.view.subscribe ?? subscribeToEmptySnapshot,
    session?.view.getSnapshot ?? getEmptySnapshot,
    getEmptySnapshot,
  )
  return (
    <EstablishedAhpSessionContext value={session}>
      <EstablishedAhpSessionSnapshotContext value={snapshot}>
        <CopilotClientRecoveryContext value={recovery}>
          <CopilotResultPresentationContext value={presentation}>{children}</CopilotResultPresentationContext>
        </CopilotClientRecoveryContext>
      </EstablishedAhpSessionSnapshotContext>
    </EstablishedAhpSessionContext>
  )
}

EstablishedAhpSessionProvider.displayName = 'EstablishedAhpSessionProvider'
